import React, { useMemo, useState } from "react";
import { useLanguage } from "../i18n/LanguageProvider";
import {
  SURVEY_OTHER_OPTION_ID,
  SURVEY_OTHER_TEXT_MAX_LENGTH,
  buildSurveySubmission,
  getSurveyQuestionValidation,
  toggleSurveySelection,
} from "../utils/surveyFormLogic";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 12000,
  display: "grid",
  placeItems: "center",
  padding: 18,
  background: "rgba(0,0,0,0.72)",
  overflowY: "auto",
};
const modalStyle = {
  position: "relative",
  width: "min(100%, 620px)",
  maxHeight: "calc(100dvh - 36px)",
  overflowY: "auto",
  boxSizing: "border-box",
  borderRadius: 18,
  border: "2px solid rgba(255,255,255,0.42)",
  padding: 20,
  color: "white",
  background: "linear-gradient(160deg, #23164f 0%, #12083b 100%)",
  boxShadow: "0 24px 70px rgba(0,0,0,0.56)",
};
const buttonStyle = {
  flex: "1 1 180px",
  minHeight: 48,
  borderRadius: 999,
  border: "2px solid rgba(255,255,255,0.42)",
  color: "white",
  background: "linear-gradient(135deg, #ff007f, #8b2cff)",
  fontWeight: 1000,
  textTransform: "uppercase",
  cursor: "pointer",
};

export default function SurveyModal({ survey, onDefer, onSubmit, busy = false }) {
  const { language, t } = useLanguage();
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [otherTexts, setOtherTexts] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [message, setMessage] = useState("");
  const questions = useMemo(() => Array.isArray(survey?.questions) ? survey.questions : [], [survey]);
  const localized = (value) => value?.[language] || value?.de || value?.en || "";
  const question = questions[questionIndex] || null;
  const selections = question ? (answers[question.questionId] || []) : [];
  const progress = questions.length ? ((questionIndex + 1) / questions.length) * 100 : 0;

  const toggle = (question, optionId) => {
    const existing = Array.isArray(answers[question.questionId]) ? answers[question.questionId] : [];
    const nextSelections = toggleSurveySelection(question, existing, optionId);
    setAnswers((current) => ({ ...current, [question.questionId]: nextSelections }));
    if (!nextSelections.includes(SURVEY_OTHER_OPTION_ID)) {
      setOtherTexts((texts) => {
        const updated = { ...texts };
        delete updated[question.questionId];
        return updated;
      });
    }
    setMessage("");
  };

  const validateCurrent = () => {
    const error = getSurveyQuestionValidation(
      question,
      selections,
      otherTexts[question?.questionId]
    );
    if (error) setMessage(t(`surveys.${error}`));
    return !error;
  };

  const next = () => {
    if (!validateCurrent()) return;
    setMessage("");
    setQuestionIndex((index) => Math.min(index + 1, questions.length - 1));
  };

  const submit = async () => {
    if (!validateCurrent()) return;
    const payload = buildSurveySubmission(answers, otherTexts);
    await onSubmit(payload.answers, payload.otherTexts);
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="vq-survey-title">
      <div style={modalStyle}>
        {!started ? <>
          <h2 id="vq-survey-title" style={{ margin: 0, textTransform: "uppercase", lineHeight: 1.18 }}>{t("surveys.entryTitle")}</h2>
          <p style={{ margin: "16px 0 22px", fontSize: 18, lineHeight: 1.4 }}>{localized(survey?.title)}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button type="button" disabled={busy} style={buttonStyle} onClick={() => setStarted(true)}>{t("common.ok")}</button>
            <button type="button" disabled={busy} style={{ ...buttonStyle, background: "rgba(255,255,255,0.1)" }} onClick={onDefer}>{t("surveys.notNow")}</button>
          </div>
        </> : <>
          <button type="button" aria-label={t("common.close")} disabled={busy} onClick={onDefer} style={{ position: "absolute", top: 12, right: 14, width: 40, height: 40, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.4)", color: "white", background: "rgba(255,255,255,0.1)", fontSize: 25, cursor: "pointer" }}>×</button>
          <h2 id="vq-survey-title" style={{ marginTop: 0 }}>{localized(survey?.title)}</h2>
          {question && <>
            <div style={{ fontWeight: 900, marginBottom: 7 }}>{t("surveys.questionProgress", { current: questionIndex + 1, total: questions.length })}</div>
            <div aria-hidden="true" style={{ height: 7, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,0.13)", marginBottom: 16 }}><div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg, #ff007f, #8b2cff)", transition: "width 220ms ease" }} /></div>
            <fieldset key={question.questionId} style={{ border: "1px solid rgba(255,255,255,0.26)", borderRadius: 14, padding: 14, minWidth: 0 }}>
                <legend style={{ padding: "0 7px", fontWeight: 1000 }}>{localized(question.text)}</legend>
                <div style={{ margin: "0 0 10px", color: "rgba(255,255,255,0.67)", fontSize: 13, lineHeight: 1.35 }}>
                  {t(question.type === "single" ? "surveys.selectOneHint" : "surveys.selectMultipleHint")}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {(question.options || []).map((option) => {
                    const checked = (answers[question.questionId] || []).includes(option.optionId);
                    return <label key={option.optionId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: checked ? "rgba(255,0,127,0.24)" : "rgba(255,255,255,0.07)", cursor: "pointer" }}>
                      <input type={question.type === "single" ? "radio" : "checkbox"} name={question.questionId} checked={checked} onChange={() => toggle(question, option.optionId)} />
                      <span>{localized(option.label)}</span>
                    </label>;
                  })}
                  {question.allowOther === true && <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: selections.includes(SURVEY_OTHER_OPTION_ID) ? "rgba(255,0,127,0.24)" : "rgba(255,255,255,0.07)", cursor: "pointer" }}>
                    <input type={question.type === "single" ? "radio" : "checkbox"} name={question.questionId} checked={selections.includes(SURVEY_OTHER_OPTION_ID)} onChange={() => toggle(question, SURVEY_OTHER_OPTION_ID)} />
                    <span>{t("surveys.other")}</span>
                  </label>}
                  {selections.includes(SURVEY_OTHER_OPTION_ID) && <div>
                    <textarea value={otherTexts[question.questionId] || ""} maxLength={SURVEY_OTHER_TEXT_MAX_LENGTH} onChange={(event) => { setOtherTexts((current) => ({ ...current, [question.questionId]: event.target.value })); setMessage(""); }} placeholder={t("surveys.otherPlaceholder")} style={{ width: "100%", minHeight: 90, boxSizing: "border-box", resize: "vertical", borderRadius: 10, padding: 11, font: "inherit" }} />
                    <div style={{ marginTop: 5, color: "rgba(255,255,255,0.67)", fontSize: 13 }}>{t("surveys.noPersonalData")}</div>
                  </div>}
                </div>
            </fieldset>
          </>}
          {message && <div style={{ marginTop: 12, color: "#ffb4c8", fontWeight: 800 }}>{message}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            {questionIndex > 0 && <button type="button" disabled={busy} style={{ ...buttonStyle, background: "rgba(255,255,255,0.1)" }} onClick={() => { setMessage(""); setQuestionIndex((index) => index - 1); }}>{t("common.back")}</button>}
            <button type="button" disabled={busy} style={buttonStyle} onClick={questionIndex === questions.length - 1 ? submit : next}>{busy ? t("common.loading") : t(questionIndex === questions.length - 1 ? "surveys.submit" : "surveys.next")}</button>
          </div>
        </>}
      </div>
    </div>
  );
}
